import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Download, CloudUpload, ChevronDown, ChevronUp } from 'lucide-react';
import { MAIL_API, mailAuth, attachmentUrl, fmtSize, getMailbox } from './mailUtils';
import { DriveFolderPopover } from './SaveToDrive';

/** Every attachment on the conversation in one strip: download all (zip) or save all to the job folder. */
export const AttachmentsBar = ({ threadId, messages, chips }) => {
  const [open, setOpen] = useState(false);
  const [drive, setDrive] = useState(false);
  const [saving, setSaving] = useState(false);
  const btnRef = useRef(null);
  const atts = messages.flatMap(m => (m.attachments || []).map(a => ({ ...a, from: m.from, date: m.date })));
  if (!atts.length) return null;
  const total = atts.reduce((s, a) => s + (a.size || 0), 0);
  const zipUrl = `${MAIL_API}/threads/${threadId}/attachments.zip?token=${localStorage.getItem('token')}${getMailbox() ? `&mailbox=${encodeURIComponent(getMailbox())}` : ''}`;

  const saveAll = async (folderId, target, ref) => {
    setSaving(true);
    try {
      const r = await axios.post(`${MAIL_API}/threads/${threadId}/attachments/to-drive`, { folder_id: folderId, ref }, { ...mailAuth(), timeout: 300000 });
      const sub = target?.subfolders?.find(f => f.id === folderId);
      toast.success(`Saved ${r.data.count} file${r.data.count === 1 ? '' : 's'} to ${target?.label} › ${sub ? sub.name : target?.name}`, { action: { label: 'Open folder', onClick: () => window.open(target?.url, '_blank') } });
      setDrive(false);
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'object' ? d.message : d || 'Could not save to Drive'); }
    setSaving(false);
  };

  return (
    <div className="mx-4 mt-3 rounded-xl border border-slate-200 bg-white/80" data-testid="mail-attachments-bar">
      <div className="flex items-center gap-2 px-3 py-2 flex-wrap">
        <Paperclip className="w-4 h-4 text-sky-600" />
        <span className="text-xs font-bold text-slate-900" data-testid="mail-attachments-count">{atts.length} attachment{atts.length === 1 ? '' : 's'}</span>
        <span className="text-[11px] text-slate-500">{fmtSize(total)}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <a href={zipUrl} className="px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-sky-300 hover:bg-sky-50 text-xs text-slate-800 font-semibold flex items-center gap-1.5 shadow-sm" data-testid="mail-download-all"><Download className="w-3.5 h-3.5" />Download all (.zip)</a>
          <span className="relative inline-flex">
            <button ref={btnRef} onClick={() => setDrive(o => !o)} className="px-2.5 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold flex items-center gap-1.5 shadow-sm" data-testid="mail-save-all-drive"><CloudUpload className="w-3.5 h-3.5" />Save all to Drive</button>
            <DriveFolderPopover open={drive} onClose={() => setDrive(false)} anchorRef={btnRef} chips={chips} saving={saving} onConfirm={saveAll} confirmLabel={`Save all ${atts.length} files`} testid="mail-save-all" />
          </span>
          <button onClick={() => setOpen(o => !o)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title={open ? 'Hide list' : 'Show list'} data-testid="mail-attachments-toggle">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
        </div>
      </div>
      {open && (
        <ul className="border-t border-slate-100 divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="mail-attachments-list">
          {atts.map(a => (
            <li key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
              <a href={attachmentUrl(a)} target="_blank" rel="noreferrer" className="text-slate-800 hover:text-sky-700 truncate flex-1">{a.filename}</a>
              <span className="text-slate-500 shrink-0">{fmtSize(a.size)}</span>
              <span className="text-slate-400 shrink-0 hidden sm:inline">{a.from?.name || a.from?.email}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
